import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

const axios = require('axios').default;
import AsyncSelect from 'react-select/async';
import {EditorState} from 'draft-js';
import {stateFromHTML} from 'draft-js-import-html';

import FinanceItemTemplate from './FinanceItemTemplate';
import NoteTemplateForm from './NoteTemplateForm';
import V3OfferTemplateEditDrawer from '../../OfferEditUI/components/V3OfferTemplateEditDrawer';
import V3BudgetTemplateEditDrawer from '../../OfferEditUI/components/V3BudgetTemplateEditDrawer';
import { budgetTemplateOptions } from '../utils/budgetTemplateOptions';
import darkSelectStyles, { mergeSelectStyles } from '../../../shared/darkSelectStyles';
import '../../TeamManager/styles/teamManagerStyles.css';
import '../styles/eventTemplatesManager.css';

const selectStyles = mergeSelectStyles({
  container: base => ({ ...base, zIndex: "999" })
});

const shouldDiscardLineItem = (lineItem) => {
  return (
    (lineItem.category_id === null || lineItem.category_id === "")
    && (lineItem.description === null || lineItem.description === "")
    && (lineItem.amount === null || lineItem.amount === "" || lineItem.amount === "0.00")
    && (lineItem.estimate === null || lineItem.estimate === "" || lineItem.estimate === "0.00")
    && (lineItem.actual === null || lineItem.actual === "" || lineItem.actual === "0.00")
  );
};

const canSubmitForm = (eventTemplate, eventTemplateForFormChanged) => {
  var errors = [];

  if(eventTemplate.name === null || eventTemplate.name === "") {
    errors.push("name");
  }

  // O-5024: a template captured from the V3 offer editor carries no V2 finance
  // halves, and ActiveModel::Serialization omits a nil singular association
  // entirely — so these keys are ABSENT, not empty. Without the guard, Save threw
  // before issuing any request: no toast, no save, nothing.
  (eventTemplate.income_template?.finance_line_items || [])
    .filter(lineItem => (!lineItem._destroy && !shouldDiscardLineItem(lineItem)))
    .forEach((lineItem, index) => {
      if(!lineItem.category_id || lineItem.category_id === "") {
        errors.push(`incomeLineItemCategory${index}`);
      }

      if(lineItem.description === null || lineItem.description === "") {
        errors.push(`incomeLineItemDescription${index}`);
      }
    });

  (eventTemplate.expense_template?.finance_line_items || [])
    .filter(lineItem => (!lineItem._destroy && !shouldDiscardLineItem(lineItem)))
    .forEach((lineItem, index) => {
      if(!lineItem.category_id || lineItem.category_id === "") {
        errors.push(`expenseLineItemCategory${index}`);
      }

      if(lineItem.description === null || lineItem.description === "") {
        errors.push(`expenseLineItemDescription${index}`);
      }
    });

  eventTemplateForFormChanged({
    ...eventTemplate,
    errors: errors
  });

  return errors.length === 0;
}

const debouncedFetchNoteTemplates = _.debounce((teamId, inputValue, callback) => {
  new Promise(resolve => {
    axios.get(`/teams/${teamId}/note_templates`, {
      params: {
        name: inputValue
      }
    })
    .then(function (response) {
      var newOptions = response.data.sort((a, b) => {
        if(a.title.toLowerCase() < b.title.toLowerCase()) { return -1; }
        if(a.title.toLowerCase() > b.title.toLowerCase()) { return 1; }
        return 0;
      }).map(function(data){
        return { value: data.id, label: data.title };
      });

      callback(newOptions);
    })
  });
}, 250);

const LegacyEventTemplatesManager = ({
  csrfToken,
  teamId,
  eventTemplates,
  eventTemplatesChanged,
  eventTemplateForForm,
  eventTemplateForFormChanged,
  financesVisible,
  financesVisibleChanged,
  financeCategories,
  financeCategoriesChanged,
  notesVisible,
  notesVisibleChanged,
  noteTemplates,
  noteTemplatesChanged,
  createEventTemplate,
  updateEventTemplate,
  deleteEventTemplate,
  team
}) => {
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [budgetTemplates, setBudgetTemplates] = useState([]);
  const [editingOfferTemplateId, setEditingOfferTemplateId] = useState(null);
  const [editingBudgetTemplateId, setEditingBudgetTemplateId] = useState(null);

  useEffect(() => {
    if (!team.package.offer_v3) return;

    axios.get(`/teams/${teamId}/offer_templates/v3.json`)
      .then(res => {
        setOfferTemplates((res.data || []).map(t => ({ value: t.id, label: t.name })));
      });

    axios.get(`/teams/${teamId}/budget_templates/v3.json`)
      .then(res => {
        setBudgetTemplates((res.data || []).map(t => ({ value: t.id, label: t.name })));
      });
  }, [teamId, team.package.offer_v3]);

  return (
    <>
      <table className={`mb-0 table table-borderless ${eventTemplates.length > 0 ? "d-table" : "d-none"}`}>
        <colgroup>
          <col style={{"width": "40%"}} />
          <col style={{"width": "40%"}} />
          <col style={{"width": "20%"}} />
        </colgroup>
        <thead>
          <tr className="text-muted text-uppercase" style={{"fontSize": "12px"}}>
            <th>Name</th>
            <th>Description</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {eventTemplates.map((eventTemplate, index) => (
            <tr
              key={index}
              className={index > 0 ? "event-template-row" : ""}
              style={index > 0 ? {
                "borderTop": "1px solid #ccc",
              } : {}}
            >
              {eventTemplate.id !== eventTemplateForForm.id ? (
                <>
                  <td>{eventTemplate.name}</td>
                  <td>{eventTemplate.description}</td>
                  <td className="text-right">
                    <a
                      href="#"
                      className="text-muted"
                      onClick={e => {
                        e.preventDefault();

                        eventTemplateForFormChanged(eventTemplate);
                      }}
                    >
                      <i className="far fa-pencil"></i>
                    </a>
                    <a
                      href="#"
                      className="text-muted"
                      onClick={e => {
                        e.preventDefault();

                        deleteEventTemplate(csrfToken, teamId, eventTemplate, eventTemplates);
                      }}
                    >
                      <i className="ml-2 far fa-trash"></i>
                    </a>
                  </td>
                </>
              ) : (
                <td colSpan="3" className="p-0">
                  <div
                    className="mb-0 p-4 w-100 form-group event-template-form-panel"
                    style={{"backgroundColor": "#f3f9fc"}}
                  >
                    <form>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <label className="mb-0 text-muted text-uppercase">Name</label>
                          <input
                            type="text"
                            className="d-block w-100 rounded-sm pl-2 event-template-input"
                            style={{
                              height: "38px",
                              border: eventTemplateForForm.errors?.includes('name')
                                ? "2px solid #FF595E"
                                : "1px solid #e6e6e6"
                            }}
                            value={eventTemplateForForm.name}
                            onChange={e => {
                              eventTemplateForFormChanged({
                                ...eventTemplateForForm,
                                name: e.target.value,
                                errors: eventTemplateForForm.errors?.filter(error => error !== 'name'),
                              });
                            }}
                          />
                        </div>
                        <div className="ml-4 flex-grow-1">
                          <label className="mb-0 text-muted text-uppercase">Description</label>
                          <input
                            type="text"
                            className="d-block w-100 rounded-sm pl-2 event-template-input"
                            style={{"height":"38px", "border":"1px solid #e6e6e6"}}
                            value={eventTemplateForForm.description}
                            onChange={e => {
                              eventTemplateForFormChanged({
                                ...eventTemplateForForm,
                                description: e.target.value
                              });
                            }}
                          />
                        </div>
                        <div className="ml-4 pt-4 d-flex flex-grow-1">
                          <button
                            type="button"
                            className="team-btn-secondary ml-auto"
                            onClick={e => {
                              e.preventDefault();

                              if(eventTemplateForForm.id.toString().includes("new-")) {
                                eventTemplatesChanged(eventTemplates.filter(et => !et.id.toString().includes("new-")));
                              }

                              eventTemplateForFormChanged({});
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="team-btn-primary ml-2"
                            onClick={e => {
                              e.preventDefault();

                              if(!canSubmitForm(eventTemplateForForm, eventTemplateForFormChanged)){
                                return false;
                              }

                              // Spread each finance half only when it exists — a
                              // template captured from the V3 offer editor has
                              // neither. Omitting the key leaves the server's
                              // assign_attributes untouched, which is correct.
                              const updatedEventTemplateForForm = {
                                ...eventTemplateForForm,
                                ...(eventTemplateForForm.income_template ? {
                                  income_template: {
                                    ...eventTemplateForForm.income_template,
                                    finance_line_items: (eventTemplateForForm.income_template.finance_line_items || []).filter(lineItem => !shouldDiscardLineItem(lineItem))
                                  }
                                } : {}),
                                ...(eventTemplateForForm.expense_template ? {
                                  expense_template: {
                                    ...eventTemplateForForm.expense_template,
                                    finance_line_items: (eventTemplateForForm.expense_template.finance_line_items || []).filter(lineItem => !shouldDiscardLineItem(lineItem))
                                  }
                                } : {})
                              };

                              if (eventTemplateForForm.id > 0) {
                                updateEventTemplate(csrfToken, teamId, updatedEventTemplateForForm, eventTemplates);
                              } else {
                                createEventTemplate(csrfToken, teamId, updatedEventTemplateForForm, eventTemplates);
                              }
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      <div className="w-100 d-flex mt-4">
                        <div
                          className="mb-0 py-2 w-100 d-flex align-items-center justify-content-between event-template-section-header"
                          style={{
                            "fontSize": "14px",
                            "fontWeight": "bold",
                            "borderBottom": "1px solid #e6e6e6",
                            "cursor": "pointer"
                          }}
                          onClick={e => {
                            e.preventDefault();

                            notesVisibleChanged(!notesVisible);
                          }}
                        >
                          <div>Notes</div>
                          <div>
                            {notesVisible ? (
                              <i className="fas fa-caret-up"></i>
                            ) : (
                              <i className="fas fa-caret-down"></i>
                            )}
                          </div>
                        </div>
                      </div>
                      {notesVisible ? (
                        <div>
                          <AsyncSelect
                            id="note-templates"
                            isMulti
                            placeholder={"Select one or more note templates"}
                            styles={selectStyles}
                            menuPortalTarget={document.querySelector('body')}
                            isClearable={true}
                            defaultOptions={[...noteTemplates].sort((a, b) => {
                              if(a.title.toLowerCase() < b.title.toLowerCase()) { return -1; }
                              if(a.title.toLowerCase() > b.title.toLowerCase()) { return 1; }
                              return 0;
                            }).map(note => (
                              { value: note.id, label: note.title }
                            ))}
                            value={eventTemplateForForm.notes
                              .filter(note => !note._destroy)
                              .map(note => (
                                { value: note.id, label: note.title }
                              ))
                            }
                            loadOptions={(inputValue, callback) => debouncedFetchNoteTemplates(teamId, inputValue, callback)}
                            onChange={options => {
                              options = options || [];
                              const activeNoteTemplates = eventTemplateForForm.notes.filter(note => !note._destroy)
                              if(options.length > activeNoteTemplates.length) {
                                eventTemplateForFormChanged({
                                  ...eventTemplateForForm,
                                  notes: noteTemplates.filter(noteTemplate => {
                                    return options.find(option => option.value === noteTemplate.id)
                                  }).map(noteTemplate => (
                                    { id: noteTemplate.id, title: noteTemplate.title, body: noteTemplate.body }
                                  ))
                                });
                              } else if(options.length < activeNoteTemplates.length) {
                                eventTemplateForFormChanged({
                                  ...eventTemplateForForm,
                                  notes: eventTemplateForForm.notes.map(note => {
                                    if(options.find(option => option.value === note.id)) {
                                      return { id: note.id, title: note.title };
                                    } else {
                                      return { value: note.id, title: note.title, _destroy: true };
                                    }
                                  })
                                });
                              }
                            }}
                          />
                          <div className="mt-3">
                            <NoteTemplateForm
                              csrfToken={csrfToken}
                              teamId={teamId}
                              noteTemplates={noteTemplates}
                              noteTemplatesChanged={noteTemplatesChanged}
                              eventTemplateForForm={eventTemplateForForm}
                              eventTemplateForFormChanged={eventTemplateForFormChanged}
                            />
                          </div>
                        </div>
                      ): null}
                      {team.package.pipeline ? (
                        <>
                          {team.package.offer_v3 ? (
                            <>
                              <div className="w-100 d-flex mt-4">
                                <div
                                  className="mb-0 py-2 w-100 d-flex align-items-center justify-content-between event-template-section-header"
                                  style={{
                                    "fontSize": "14px",
                                    "fontWeight": "bold",
                                    "borderBottom": "1px solid #e6e6e6",
                                    "cursor": "pointer"
                                  }}
                                  onClick={e => {
                                    e.preventDefault();
                                    financesVisibleChanged(!financesVisible);
                                  }}
                                >
                                  <div>Templates</div>
                                  <div>
                                    {financesVisible ? (
                                      <i className="fas fa-caret-up"></i>
                                    ) : (
                                      <i className="fas fa-caret-down"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {financesVisible ? (
                                <div className="mt-3">
                                  <div className="form-group">
                                    <label className="mb-1 text-muted text-uppercase" style={{"fontSize": "12px"}}>
                                      Offer Template
                                    </label>
                                    <select
                                      className="form-control"
                                      value={eventTemplateForForm.v3_offer_template_id || ""}
                                      onChange={e => {
                                        eventTemplateForFormChanged({
                                          ...eventTemplateForForm,
                                          v3_offer_template_id: e.target.value ? parseInt(e.target.value) : null
                                        });
                                      }}
                                    >
                                      <option value="">None</option>
                                      {offerTemplates.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                      ))}
                                    </select>
                                    {eventTemplateForForm.v3_offer_template_id ? (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 mt-1"
                                        onClick={() => setEditingOfferTemplateId(eventTemplateForForm.v3_offer_template_id)}
                                      >
                                        Edit deal terms
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className="form-group">
                                    <label className="mb-1 text-muted text-uppercase" style={{"fontSize": "12px"}}>
                                      Budget Template
                                    </label>
                                    <select
                                      className="form-control"
                                      value={eventTemplateForForm.v3_budget_template_id || ""}
                                      onChange={e => {
                                        eventTemplateForFormChanged({
                                          ...eventTemplateForForm,
                                          v3_budget_template_id: e.target.value ? parseInt(e.target.value) : null
                                        });
                                      }}
                                    >
                                      <option value="">None</option>
                                      {budgetTemplateOptions(budgetTemplates, eventTemplateForForm).map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                      ))}
                                    </select>
                                    {eventTemplateForForm.v3_budget_template_id ? (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 mt-1"
                                        onClick={() => setEditingBudgetTemplateId(eventTemplateForForm.v3_budget_template_id)}
                                      >
                                        Edit line items
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}
                              <V3OfferTemplateEditDrawer
                                team={{ id: teamId }}
                                templateId={editingOfferTemplateId}
                                open={editingOfferTemplateId !== null}
                                onClose={() => setEditingOfferTemplateId(null)}
                                onSaved={() => setEditingOfferTemplateId(null)}
                                csrfToken={csrfToken}
                                contextLabel="Event template › offer template"
                              />
                              <V3BudgetTemplateEditDrawer
                                team={{ id: teamId }}
                                templateId={editingBudgetTemplateId}
                                open={editingBudgetTemplateId !== null}
                                onClose={() => setEditingBudgetTemplateId(null)}
                                csrfToken={csrfToken}
                                currentUser={{}}
                              />
                            </>
                          ) : (
                            <>
                              <div className="w-100 d-flex mt-4">
                                <div
                                  className="mb-0 py-2 w-100 d-flex align-items-center justify-content-between event-template-section-header"
                                  style={{
                                    "fontSize": "14px",
                                    "fontWeight": "bold",
                                    "borderBottom": "1px solid #e6e6e6",
                                    "cursor": "pointer"
                                  }}
                                  onClick={e => {
                                    e.preventDefault();
                                    financesVisibleChanged(!financesVisible);
                                  }}
                                >
                                  <div>Finances</div>
                                  <div>
                                    {financesVisible ? (
                                      <i className="fas fa-caret-up"></i>
                                    ) : (
                                      <i className="fas fa-caret-down"></i>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {financesVisible ? (
                                <div>
                                  <div
                                    className="my-4 font-weight-bold text-uppercase"
                                    style={{"fontSize": "12px"}}
                                  >
                                    Income
                                  </div>
                                  {eventTemplateForForm.income_template ? (
                                    <div className="mb-5">
                                      <FinanceItemTemplate
                                        csrfToken={csrfToken}
                                        teamId={teamId}
                                        financeItem={eventTemplateForForm.income_template}
                                        eventTemplateForForm={eventTemplateForForm}
                                        eventTemplateForFormChanged={eventTemplateForFormChanged}
                                        financeCategories={financeCategories}
                                        financeCategoriesChanged={financeCategoriesChanged}
                                      />
                                    </div>
                                  ) : null}
                                  <div
                                    className="my-4 font-weight-bold text-uppercase"
                                    style={{"fontSize": "12px"}}
                                  >
                                    Expense
                                  </div>
                                  {eventTemplateForForm.expense_template ? (
                                    <div className="mb-2">
                                      <FinanceItemTemplate
                                        csrfToken={csrfToken}
                                        teamId={teamId}
                                        financeItem={eventTemplateForForm.expense_template}
                                        eventTemplateForForm={eventTemplateForForm}
                                        eventTemplateForFormChanged={eventTemplateForFormChanged}
                                        financeCategories={financeCategories}
                                        financeCategoriesChanged={financeCategoriesChanged}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </>
                          )}
                        </>
                      ) : null}
                    </form>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={e => {
            e.preventDefault();

            const newEventTemplate = {
              id: `new-${eventTemplates.length}`,
              name: '',
              description: '',
              notes: [],
              v3_offer_template_id: null,
              v3_budget_template_id: null,
              income_template: {
                is_income: true,
                finance_line_items: []
              },
              expense_template: {
                is_income: false,
                finance_line_items: []
              }
            };

            eventTemplateForFormChanged(newEventTemplate);
            eventTemplatesChanged([...eventTemplates, newEventTemplate]);
          }}
        >
          <i className="far fa-plus"></i> New Template
        </button>
      </div>
    </>
  );
}

LegacyEventTemplatesManager.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  eventTemplates: PropTypes.array.isRequired,
  eventTemplatesChanged: PropTypes.func.isRequired,
  eventTemplateForForm: PropTypes.object.isRequired,
  eventTemplateForFormChanged: PropTypes.func.isRequired,
  financesVisible: PropTypes.bool.isRequired,
  financesVisibleChanged: PropTypes.func.isRequired,
  financeCategories: PropTypes.array.isRequired,
  financeCategoriesChanged: PropTypes.func.isRequired,
  notesVisible: PropTypes.bool.isRequired,
  notesVisibleChanged: PropTypes.func.isRequired,
  noteTemplates: PropTypes.array.isRequired,
  noteTemplatesChanged: PropTypes.func.isRequired,
  createEventTemplate: PropTypes.func.isRequired,
  updateEventTemplate: PropTypes.func.isRequired,
  deleteEventTemplate: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired
}

export default LegacyEventTemplatesManager;
